import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Key, Eye, EyeOff, Check, X, Loader2, RefreshCw,
  Shield, Mail, CreditCard, Cpu, Lock, AlertTriangle,
  CheckCircle2, XCircle, Info, Github, HardDrive
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import api from '../services/api';

// Category icons mapping
const CATEGORY_ICONS = {
  'AI': Cpu,
  'Email': Mail,
  'Payments': CreditCard,
  'Access': Shield,
  'Security': Lock,
  'Authentication': Key,
  'Integrations': Github,
  'Storage': HardDrive
};

// Microsoft Logo SVG
const MicrosoftLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// Google Logo SVG
const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// GitHub Logo SVG
const GitHubLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

// Stripe Logo SVG
const StripeLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#635BFF">
    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
  </svg>
);

// Generic Integration Card Component (for GitHub, Stripe patterns)
const IntegrationCard = ({ 
  provider, 
  Logo, 
  title, 
  description, 
  fields, 
  colors, 
  onSave, 
  onRefresh,
  configuredCheck,
  additionalInfo
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});
  const [showSecrets, setShowSecrets] = useState({});

  const isConfigured = configuredCheck(fields);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(values);
      setIsEditing(false);
      setValues({});
      onRefresh();
      toast.success(`${title} settings updated successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleShowSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgSecondary)}>
            <Logo />
          </div>
          <div>
            <h3 className={cn("font-medium", colors.textPrimary)}>{title}</h3>
            <p className={cn("text-sm mt-0.5", colors.textMuted)}>{description}</p>
            <div className="flex items-center gap-2 mt-1">
              {isConfigured ? (
                <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Configured
                </Badge>
              ) : (
                <Badge variant="outline" className="text-gray-500 border-gray-300">
                  <XCircle className="w-3 h-3 mr-1" />
                  Not Configured
                </Badge>
              )}
            </div>
            {/* Show current values for text fields inline */}
            {fields.some(f => f.field_type === 'text' && f.current_value) && (
              <div className={cn("mt-2 space-y-1")}>
                {fields.filter(f => f.field_type === 'text' && f.current_value).map(f => (
                  <p key={f.env_key} className={cn("text-xs font-mono", colors.textMuted)}>
                    {f.label}: <span className={cn("font-medium", colors.textPrimary)}>{f.current_value}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? 'Cancel' : 'Configure'}
        </Button>
      </div>

      {isEditing && (
        <div className="mt-4 pt-4 border-t space-y-4">
          {fields.map((field) => (
            <div key={field.env_key}>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>
                {field.label}
                {field.is_set && field.field_type !== 'textarea' && ' (leave blank to keep existing)'}
              </label>
              <p className={cn("text-xs mb-1", colors.textMuted)}>{field.description}</p>
              
              {field.field_type === 'textarea' ? (
                <textarea
                  value={values[field.env_key] || ''}
                  onChange={(e) => setValues({ ...values, [field.env_key]: e.target.value })}
                  placeholder={field.is_set ? '••• configured (paste new to replace) •••' : `Paste your ${field.label} here`}
                  className={cn(
                    "w-full h-24 p-3 font-mono text-xs rounded-md border resize-none mt-1",
                    colors.bgSecondary, colors.borderPrimary, colors.textPrimary
                  )}
                />
              ) : field.field_type === 'select' ? (
                <Select 
                  value={values[field.env_key] || field.current_value || ''} 
                  onValueChange={(v) => setValues({ ...values, [field.env_key]: v })}
                >
                  <SelectTrigger className={cn("mt-1", colors.bgSecondary)}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option} value={option} className="capitalize">
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : field.field_type === 'text' ? (
                <Input
                  type="text"
                  value={values[field.env_key] ?? field.current_value ?? ''}
                  onChange={(e) => setValues({ ...values, [field.env_key]: e.target.value })}
                  placeholder={`Enter ${field.label}`}
                  className={cn("mt-1 font-mono text-sm", colors.bgSecondary)}
                />
              ) : (
                <div className="relative mt-1">
                  <Input
                    type={showSecrets[field.env_key] ? "text" : "password"}
                    value={values[field.env_key] || ''}
                    onChange={(e) => setValues({ ...values, [field.env_key]: e.target.value })}
                    placeholder={field.is_set ? '••••••••••••' : `Enter ${field.label}`}
                    className={cn("pr-10 font-mono text-sm", colors.bgSecondary)}
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret(field.env_key)}
                    className={cn(
                      "absolute right-2 top-1/2 -translate-y-1/2",
                      colors.textMuted, "hover:text-gray-700"
                    )}
                  >
                    {showSecrets[field.env_key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          ))}
          
          {additionalInfo && (
            <div className={cn("p-3 rounded-md text-xs", colors.bgSecondary)}>
              {additionalInfo}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setIsEditing(false); setValues({}); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Configuration
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// OAuth Provider Card Component
const OAuthProviderCard = ({ provider, colors, onUpdate, onRefresh }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    enabled: provider.enabled,
    client_id: provider.client_id || '',
    client_secret: '',
    tenant_id: provider.tenant_id || 'common',
    redirect_uri: provider.redirect_uri || '',
    scopes: provider.scopes || []
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/system-settings/oauth-providers/${provider.provider}`, {
        provider: provider.provider,
        enabled: config.enabled,
        client_id: config.client_id,
        client_secret: config.client_secret || undefined,
        tenant_id: config.tenant_id,
        redirect_uri: config.redirect_uri,
        scopes: config.scopes
      });
      setIsEditing(false);
      onRefresh();
      toast.success(`${provider.label} OAuth updated successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async () => {
    // Quick toggle without full edit
    setSaving(true);
    try {
      await api.put(`/api/admin/system-settings/oauth-providers/${provider.provider}`, {
        provider: provider.provider,
        enabled: !provider.enabled,
        client_id: provider.client_id,
        tenant_id: provider.tenant_id,
        redirect_uri: provider.redirect_uri,
        scopes: provider.scopes
      });
      onRefresh();
      toast.success(`${provider.label} OAuth ${!provider.enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle');
    } finally {
      setSaving(false);
    }
  };

  const ProviderIcon = provider.icon === 'microsoft' ? MicrosoftLogo : GoogleLogo;

  return (
    <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgSecondary)}>
            <ProviderIcon />
          </div>
          <div>
            <h3 className={cn("font-medium", colors.textPrimary)}>{provider.label} OAuth</h3>
            <div className="flex items-center gap-2 mt-1">
              {provider.status === 'configured' ? (
                <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Configured
                </Badge>
              ) : (
                <Badge variant="outline" className="text-gray-500 border-gray-300">
                  <XCircle className="w-3 h-3 mr-1" />
                  Not Configured
                </Badge>
              )}
              {provider.enabled && provider.status === 'configured' && (
                <Badge className="bg-green-600">Active</Badge>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {provider.status === 'configured' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggle}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : provider.enabled ? (
                'Disable'
              ) : (
                'Enable'
              )}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel' : 'Configure'}
          </Button>
        </div>
      </div>

      {isEditing && (
        <div className="mt-4 pt-4 border-t space-y-4">
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Client ID</label>
            <Input
              value={config.client_id}
              onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
              placeholder="Enter client ID"
              className="mt-1"
            />
          </div>
          
          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>
              Client Secret {provider.client_secret_set && '(leave blank to keep existing)'}
            </label>
            <Input
              type="password"
              value={config.client_secret}
              onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
              placeholder={provider.client_secret_set ? '••••••••••••' : 'Enter client secret'}
              className="mt-1"
            />
          </div>

          {provider.requires_tenant && (
            <div>
              <label className={cn("text-sm font-medium", colors.textSecondary)}>Tenant ID</label>
              <Input
                value={config.tenant_id}
                onChange={(e) => setConfig({ ...config, tenant_id: e.target.value })}
                placeholder="common, organizations, consumers, or specific tenant ID"
                className="mt-1"
              />
              <p className={cn("text-xs mt-1", colors.textMuted)}>
                Use "common" for any Microsoft account, "organizations" for work/school only
              </p>
            </div>
          )}

          <div>
            <label className={cn("text-sm font-medium", colors.textSecondary)}>Redirect URI</label>
            <Input
              value={`${window.location.origin}/api/auth/${provider.provider}/callback`}
              readOnly
              className="mt-1 bg-gray-50 dark:bg-white/5 cursor-default"
            />
            <p className={cn("text-xs mt-1", colors.textMuted)}>
              Add this URL to your {provider.label} app's redirect URIs. This is auto-generated from your current domain.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`enable-${provider.provider}`}
              checked={config.enabled}
              onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
              className="rounded"
            />
            <label htmlFor={`enable-${provider.provider}`} className={cn("text-sm", colors.textSecondary)}>
              Enable this provider on login page
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Configuration
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

// OAuth Providers Section Component
const OAuthProvidersSection = ({ colors }) => {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/system-settings/oauth-providers');
      setProviders(response.data.providers || []);
    } catch (err) {
      console.error('Failed to fetch OAuth providers:', err);
      toast.error('Failed to load OAuth providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Key className={cn("w-4 h-4", colors.textMuted)} />
        <h2 className={cn("text-sm font-semibold uppercase tracking-wider", colors.textMuted)}>
          OAuth Providers
        </h2>
      </div>
      <div className="space-y-3">
        {providers.map((provider) => (
          <OAuthProviderCard
            key={provider.provider}
            provider={provider}
            colors={colors}
            onRefresh={fetchProviders}
          />
        ))}
      </div>
      <p className={cn("text-xs", colors.textMuted)}>
        Configure OAuth providers to allow users to sign in with their existing accounts.
        Enabled providers will appear on the login page.
      </p>
    </div>
  );
};

// GitHub App Section Component
const GitHubAppSection = ({ colors, settings, onRefresh }) => {
  const [saving, setSaving] = useState(false);

  // Get GitHub-related fields from settings
  const githubFields = settings?.api_keys?.filter(k => 
    k.env_key?.startsWith('GITHUB_')
  ) || [];

  const handleSave = async (values) => {
    // Save each field that has a value
    for (const [envKey, value] of Object.entries(values)) {
      if (value && value.trim()) {
        await api.put(`/api/admin/system-settings/${envKey}`, { env_key: envKey, value });
      }
    }
    await onRefresh();
  };

  const isConfigured = (fields) => {
    const requiredFields = ['GITHUB_APP_ID', 'GITHUB_CLIENT_ID', 'GITHUB_PRIVATE_KEY'];
    return requiredFields.every(key => fields.find(f => f.env_key === key)?.is_set);
  };

  if (githubFields.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Github className={cn("w-4 h-4", colors.textMuted)} />
        <h2 className={cn("text-sm font-semibold uppercase tracking-wider", colors.textMuted)}>
          GitHub App
        </h2>
      </div>
      <IntegrationCard
        provider="github"
        Logo={GitHubLogo}
        title="GitHub App"
        description="Connect GitHub for source control and issue tracking"
        fields={githubFields}
        colors={colors}
        onSave={handleSave}
        onRefresh={onRefresh}
        configuredCheck={isConfigured}
        additionalInfo={
          <div className={cn("text-xs space-y-1", colors.textMuted)}>
            <p><strong>Webhook URL:</strong> {window.location.origin}/api/integrations/github/webhook</p>
            <p><strong>Callback URL:</strong> {window.location.origin}/api/integrations/github/callback</p>
          </div>
        }
      />
    </div>
  );
};

// Stripe Section Component
const StripeSectionCard = ({ colors, settings, onRefresh }) => {
  const [saving, setSaving] = useState(false);
  const [activeMode, setActiveMode] = useState('test');

  // Get Stripe-related fields from settings
  const allStripeFields = settings?.api_keys?.filter(k => 
    k.env_key?.startsWith('STRIPE_')
  ) || [];

  // Get mode field
  const modeField = allStripeFields.find(f => f.env_key === 'STRIPE_MODE');
  
  // Separate test and live fields
  const testFields = allStripeFields.filter(f => f.env_key?.includes('_TEST_'));
  const liveFields = allStripeFields.filter(f => f.env_key?.includes('_LIVE_'));

  useEffect(() => {
    if (modeField?.current_value) {
      setActiveMode(modeField.current_value);
    }
  }, [modeField?.current_value]);

  const handleSave = async (values) => {
    for (const [envKey, value] of Object.entries(values)) {
      if (value && value.trim()) {
        await api.put(`/api/admin/system-settings/${envKey}`, { env_key: envKey, value });
      }
    }
    await onRefresh();
  };

  const handleModeChange = async (newMode) => {
    try {
      await api.put('/api/admin/system-settings/STRIPE_MODE', { env_key: 'STRIPE_MODE', value: newMode });
      setActiveMode(newMode);
      await onRefresh();
      toast.success(`Switched to ${newMode.toUpperCase()} mode`);
    } catch (err) {
      toast.error('Failed to switch mode');
    }
  };

  const isConfigured = (fields) => {
    return fields.some(f => f.is_set);
  };

  if (allStripeFields.length === 0) return null;

  const currentFields = activeMode === 'test' ? testFields : liveFields;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <CreditCard className={cn("w-4 h-4", colors.textMuted)} />
        <h2 className={cn("text-sm font-semibold uppercase tracking-wider", colors.textMuted)}>
          Payments
        </h2>
      </div>
      <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colors.bgSecondary)}>
              <StripeLogo />
            </div>
            <div>
              <h3 className={cn("font-medium", colors.textPrimary)}>Stripe</h3>
              <p className={cn("text-sm mt-0.5", colors.textMuted)}>Payment processing for subscriptions and one-time charges</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge 
                  className={cn(
                    "cursor-pointer transition-all",
                    activeMode === 'test' 
                      ? "bg-blue-600 hover:bg-blue-700" 
                      : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  )}
                  onClick={() => handleModeChange('test')}
                >
                  Test Mode
                </Badge>
                <Badge 
                  className={cn(
                    "cursor-pointer transition-all",
                    activeMode === 'live' 
                      ? "bg-green-600 hover:bg-green-700" 
                      : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  )}
                  onClick={() => handleModeChange('live')}
                >
                  Live Mode
                </Badge>
                {isConfigured(currentFields) && (
                  <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 ml-2">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {activeMode === 'test' ? 'Test' : 'Live'} Configured
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t">
          <div className={cn(
            "p-3 rounded-md mb-4 text-sm",
            activeMode === 'test' ? "bg-blue-50 text-blue-800 border border-blue-200" : "bg-green-50 text-green-800 border border-green-200"
          )}>
            {activeMode === 'test' ? (
              <>
                <strong>Test Mode:</strong> Use test card 4242 4242 4242 4242 for testing. No real charges.
              </>
            ) : (
              <>
                <strong>⚠️ Live Mode:</strong> Real credit cards will be charged. Ensure your keys are correct.
              </>
            )}
          </div>
          
          {/* Webhook Endpoint Helper */}
          <div className={cn(
            "p-3 rounded-md mb-4 text-sm",
            "bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
            colors.textSecondary
          )}>
            <strong className={colors.textPrimary}>Webhook Endpoint:</strong>
            <code className="ml-2 px-2 py-1 bg-gray-100 dark:bg-gray-900 rounded text-xs font-mono">
              https://stackranked.ai/api/stripe/webhook
            </code>
            <p className="mt-2 text-xs">
              Configure this URL in your Stripe Dashboard → Developers → Webhooks to receive payment events.
            </p>
          </div>
          
          <StripeFieldsEditor 
            fields={currentFields} 
            colors={colors} 
            onSave={handleSave}
            mode={activeMode}
          />

          {/* Webhook IP Whitelist */}
          <WebhookIpWhitelist colors={colors} settings={settings} />
        </div>
      </Card>
    </div>
  );
};

// Stripe Fields Editor Sub-component
const StripeFieldsEditor = ({ fields, colors, onSave, mode }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [values, setValues] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(values);
      setIsEditing(false);
      setValues({});
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {fields.map((field) => (
        <div key={field.env_key} className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn("text-sm font-medium", colors.textPrimary)}>
                {field.label.replace('Stripe ', '').replace(mode === 'test' ? 'Test ' : 'Live ', '')}
              </span>
              <Badge variant={field.is_set ? "default" : "outline"} className="text-xs">
                {field.is_set ? 'Set' : 'Not Set'}
              </Badge>
            </div>
            {field.is_set && field.masked_value && (
              <div className={cn("mt-1 font-mono text-xs", colors.textMuted)}>
                {field.masked_value}
              </div>
            )}
          </div>
          
          {isEditing ? (
            <div className="flex items-center gap-2 w-80">
              <div className="relative flex-1">
                <Input
                  type={showSecrets[field.env_key] ? "text" : "password"}
                  value={values[field.env_key] || ''}
                  onChange={(e) => setValues({ ...values, [field.env_key]: e.target.value })}
                  placeholder={field.is_set ? 'Leave blank to keep' : `Enter key`}
                  className={cn("pr-10 font-mono text-xs", colors.bgSecondary)}
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets(prev => ({ ...prev, [field.env_key]: !prev[field.env_key] }))}
                  className={cn("absolute right-2 top-1/2 -translate-y-1/2", colors.textMuted)}
                >
                  {showSecrets[field.env_key] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ))}
      
      <div className="flex justify-end gap-2 pt-2">
        {isEditing ? (
          <>
            <Button variant="outline" size="sm" onClick={() => { setIsEditing(false); setValues({}); }}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
            Configure Keys
          </Button>
        )}
      </div>
    </div>
  );
};

// Webhook IP Whitelist component
const WebhookIpWhitelist = ({ colors, settings }) => {
  const [whitelistJson, setWhitelistJson] = useState('');
  const [ipCount, setIpCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [testingIp, setTestingIp] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Load existing whitelist from settings
  useEffect(() => {
    const existing = settings?.api_keys?.find(k => k.env_key === 'STRIPE_WEBHOOK_IP_WHITELIST');
    if (existing?.current_value) {
      setWhitelistJson(existing.current_value);
      try {
        const data = JSON.parse(existing.current_value);
        const ips = data.WEBHOOKS || data;
        setIpCount(Array.isArray(ips) ? ips.length : 0);
      } catch { setIpCount(0); }
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post('/api/stripe/webhook-ip-whitelist', { whitelist_json: whitelistJson });
      setIpCount(res.data.ip_count);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await api.post('/api/stripe/webhook-ip-whitelist', { whitelist_json: '' });
      setWhitelistJson('');
      setIpCount(0);
      toast.success('Whitelist cleared');
    } catch (err) {
      toast.error('Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const handleTestIp = async () => {
    setTestingIp(true);
    try {
      const res = await api.get('/api/stripe/webhook-test-ip');
      setTestResult(res.data);
    } catch (err) {
      toast.error('Failed to test IP');
    } finally {
      setTestingIp(false);
    }
  };

  return (
    <div className={cn("mt-4 pt-4 border-t", colors.borderPrimary)}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className={cn("w-4 h-4", colors.textMuted)} />
          <h4 className={cn("text-sm font-medium", colors.textPrimary)}>Webhook IP Whitelist</h4>
          {ipCount > 0 && (
            <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-xs">
              {ipCount} IPs
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleTestIp}
            disabled={testingIp}
            data-testid="test-ip-btn"
            className="text-xs"
          >
            {testingIp ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Info className="w-3 h-3 mr-1" />}
            Test IP
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs"
          >
            {isExpanded ? 'Collapse' : 'Configure'}
          </Button>
        </div>
      </div>

      {/* Test IP Result */}
      {testResult && (
        <div className={cn("p-3 rounded-lg mb-3 text-sm border", "bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800")}>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="font-medium">Detected IP:</span> <code className="ml-1">{testResult.detected_ip}</code></div>
            <div><span className="font-medium">Direct Host:</span> <code className="ml-1">{testResult.direct_client_host}</code></div>
            <div className="col-span-2"><span className="font-medium">X-Forwarded-For:</span> <code className="ml-1">{testResult.x_forwarded_for}</code></div>
            <div><span className="font-medium">Whitelist Size:</span> {testResult.whitelist_loaded}</div>
            <div><span className="font-medium">Your IP Whitelisted:</span> {String(testResult.your_ip_whitelisted)}</div>
          </div>
          <button onClick={() => setTestResult(null)} className={cn("text-xs mt-2 underline", colors.textMuted)}>Dismiss</button>
        </div>
      )}

      {isExpanded && (
        <div className="space-y-3">
          <p className={cn("text-xs", colors.textMuted)}>
            Paste the JSON from <a href="https://stripe.com/files/ips/ips_webhooks.json" target="_blank" rel="noopener noreferrer" className="underline text-blue-500">stripe.com/files/ips/ips_webhooks.json</a>. Only whitelisted IPs will reach the webhook. Leave empty to allow all (signature verification still applies).
          </p>
          <textarea
            data-testid="webhook-ip-textarea"
            value={whitelistJson}
            onChange={(e) => setWhitelistJson(e.target.value)}
            placeholder='{"WEBHOOKS": ["3.18.12.63", "3.130.192.231", ...]}'
            rows={6}
            className={cn(
              "w-full rounded-lg border px-3 py-2 font-mono text-xs resize-y",
              colors.bgSecondary, colors.borderPrimary, colors.textPrimary,
              "placeholder:text-gray-400"
            )}
          />
          <div className="flex justify-end gap-2">
            {ipCount > 0 && (
              <Button variant="outline" size="sm" onClick={handleClear} disabled={saving} className="text-xs text-red-600">
                Clear Whitelist
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={saving || !whitelistJson.trim()} className="text-xs">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Save Whitelist
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// API Key Card Component
const ApiKeyCard = ({ apiKey, colors, onUpdate, onTest }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // For select fields, initialize with current value
  const [selectValue, setSelectValue] = useState(apiKey.current_value || '');
  
  // Confirmation modal for critical changes (like Stripe mode)
  const [confirmModal, setConfirmModal] = useState(null);
  
  const CategoryIcon = CATEGORY_ICONS[apiKey.category] || Key;
  const isSelectField = apiKey.field_type === 'select';
  const isTextareaField = apiKey.field_type === 'textarea';
  const isTextField = apiKey.field_type === 'text';
  const isStripeMode = apiKey.key === 'stripe_mode';

  const handleSave = async () => {
    const valueToSave = isSelectField ? selectValue : (value || (isTextField ? apiKey.current_value : ''));
    if (!valueToSave?.trim()) {
      toast.error('Please enter a value');
      return;
    }
    
    setSaving(true);
    try {
      await onUpdate(apiKey.env_key, valueToSave);
      setIsEditing(false);
      setValue('');
      setTestResult(null);
      toast.success(`${apiKey.label} updated successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectChange = async (newValue) => {
    // For Stripe Mode, show confirmation modal
    if (isStripeMode && newValue !== selectValue) {
      setConfirmModal({ newValue, oldValue: selectValue });
      return;
    }
    
    // For other select fields, save immediately
    await performSelectSave(newValue);
  };

  const performSelectSave = async (newValue) => {
    setSelectValue(newValue);
    setSaving(true);
    try {
      await onUpdate(apiKey.env_key, newValue);
      toast.success(`${apiKey.label} updated to ${newValue}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
      setSelectValue(apiKey.current_value || ''); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmModeChange = async () => {
    if (confirmModal) {
      await performSelectSave(confirmModal.newValue);
      setConfirmModal(null);
    }
  };

  const handleCancelModeChange = () => {
    setConfirmModal(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await onTest(apiKey.env_key);
      setTestResult(result);
    } catch (err) {
      setTestResult({ valid: false, error: 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setValue('');
    setTestResult(null);
  };

  return (
    <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={cn(
            "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
            apiKey.is_set ? "bg-green-500/10 text-green-500" : "bg-gray-500/10 text-gray-500"
          )}>
            <CategoryIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className={cn("font-medium", colors.textPrimary)}>{apiKey.label}</h3>
              {!isSelectField && (
                <Badge variant={apiKey.is_set ? "default" : "outline"} className="text-xs">
                  {apiKey.is_set ? 'Configured' : 'Not Set'}
                </Badge>
              )}
            </div>
            <p className={cn("text-sm mt-0.5", colors.textMuted)}>{apiKey.description}</p>
            
            {/* Select Field - Always shown as dropdown */}
            {isSelectField && (
              <div className="mt-3 w-48">
                <Select value={selectValue} onValueChange={handleSelectChange} disabled={saving}>
                  <SelectTrigger className={cn(colors.bgSecondary)}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {apiKey.options?.map((option) => (
                      <SelectItem key={option} value={option} className="capitalize">
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Password/Text field - Show masked value */}
            {!isSelectField && !isTextareaField && !isTextField && apiKey.is_set && apiKey.masked_value && !isEditing && (
              <div className={cn("mt-2 font-mono text-sm", colors.textMuted)}>
                {apiKey.masked_value}
              </div>
            )}
            
            {/* Text field - Show actual value */}
            {isTextField && apiKey.current_value && !isEditing && (
              <div className={cn("mt-2 font-mono text-sm", colors.textPrimary)}>
                {apiKey.current_value}
              </div>
            )}
            
            {/* Textarea field - Show configured status */}
            {isTextareaField && apiKey.is_set && !isEditing && (
              <div className={cn("mt-2 font-mono text-sm", colors.textMuted)}>
                ••• Private key configured •••
              </div>
            )}
            
            {/* Edit Mode for textarea fields (like GitHub Private Key) */}
            {isTextareaField && isEditing && (
              <div className="mt-3 space-y-2">
                <textarea
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={`Paste your ${apiKey.label} here (e.g., -----BEGIN RSA PRIVATE KEY-----...)`}
                  className={cn(
                    "w-full h-32 p-3 font-mono text-xs rounded-md border resize-none",
                    colors.bgSecondary, colors.borderPrimary, colors.textPrimary
                  )}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {/* Edit Mode for password fields */}
            {!isSelectField && !isTextareaField && !isTextField && isEditing && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showValue ? "text" : "password"}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder={`Enter ${apiKey.label}`}
                      className={cn("pr-10 font-mono text-sm", colors.bgSecondary)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowValue(!showValue)}
                      className={cn(
                        "absolute right-2 top-1/2 -translate-y-1/2",
                        colors.textMuted, "hover:text-gray-700"
                      )}
                    >
                      {showValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {/* Edit Mode for text fields (plaintext, pre-populated) */}
            {isTextField && isEditing && (
              <div className="mt-3 space-y-2">
                <Input
                  type="text"
                  value={value || apiKey.current_value || ''}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={`Enter ${apiKey.label}`}
                  className={cn("font-mono text-sm", colors.bgSecondary)}
                  data-testid={`input-${apiKey.env_key}`}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel}>
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {/* Test Result */}
            {testResult && (
              <div className={cn(
                "mt-2 p-2 rounded-md text-sm flex items-center gap-2",
                testResult.valid ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
              )}>
                {testResult.valid ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                {testResult.valid ? "Key format is valid" : (testResult.error || "Invalid format")}
              </div>
            )}
          </div>
        </div>
        
        {/* Actions - Only show for non-select fields */}
        {!isEditing && !isSelectField && (
          <div className="flex gap-2 flex-shrink-0">
            {apiKey.is_set && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test"}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => {
              if (isTextField && apiKey.current_value) setValue(apiKey.current_value);
              setIsEditing(true);
            }}>
              {apiKey.is_set ? 'Update' : 'Configure'}
            </Button>
          </div>
        )}
      </div>

      {/* Stripe Mode Change Confirmation Modal */}
      <Dialog open={!!confirmModal} onOpenChange={() => setConfirmModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Stripe Mode Change
            </DialogTitle>
            <DialogDescription>
              You are about to switch from <strong className="text-gray-900">{confirmModal?.oldValue?.toUpperCase()}</strong> to{' '}
              <strong className={confirmModal?.newValue === 'live' ? "text-red-600" : "text-blue-600"}>
                {confirmModal?.newValue?.toUpperCase()}
              </strong> mode.
            </DialogDescription>
          </DialogHeader>
          
          <div className={cn(
            "p-4 rounded-lg my-2",
            confirmModal?.newValue === 'live' ? "bg-red-50 border border-red-200" : "bg-blue-50 border border-blue-200"
          )}>
            {confirmModal?.newValue === 'live' ? (
              <div className="text-sm text-red-800">
                <p className="font-semibold mb-2">⚠️ Warning: Switching to LIVE mode</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Real credit cards will be charged</li>
                  <li>Transactions cannot be easily reversed</li>
                  <li>Make sure your live Stripe keys are configured</li>
                </ul>
              </div>
            ) : (
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">ℹ️ Switching to TEST mode</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Only test cards will work (e.g., 4242 4242 4242 4242)</li>
                  <li>No real charges will be made</li>
                  <li>Good for development and testing</li>
                </ul>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelModeChange}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmModeChange}
              disabled={saving}
              className={confirmModal?.newValue === 'live' ? "bg-red-600 hover:bg-red-700" : ""}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Switch to {confirmModal?.newValue?.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const SystemSettingsPage = () => {
  const navigate = useNavigate();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/system-settings');
      setSettings(response.data);
    } catch (err) {
      console.error('[SystemSettings] Failed to fetch settings:', err);
      if (err.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/products');
      } else {
        toast.error('Failed to load system settings');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleUpdate = async (envKey, value) => {
    await api.put(`/api/admin/system-settings/${envKey}`, { env_key: envKey, value });
    await fetchSettings();
  };

  const handleTest = async (envKey) => {
    const response = await api.get(`/api/admin/system-settings/test/${envKey}`);
    return response.data;
  };

  // Group API keys by category (excluding ones handled by card components)
  const groupedKeys = React.useMemo(() => {
    if (!settings?.api_keys || !Array.isArray(settings.api_keys)) {
      return {};
    }
    // Filter out categories that are handled by dedicated card components
    const excludedCategories = ['Payments', 'Authentication', 'Integrations'];
    return settings.api_keys
      .filter(key => !excludedCategories.includes(key.category))
      .reduce((acc, key) => {
        if (!acc[key.category]) {
          acc[key.category] = [];
        }
        acc[key.category].push(key);
        return acc;
      }, {});
  }, [settings]);

  if (loading) {
    return (
      <div className={cn("flex h-screen", colors.bgPrimary)}>
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header title="System Settings" />
          <main className="flex-1 flex items-center justify-center">
            <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="System Settings" />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>System Settings</h1>
                <p className={cn("text-sm", colors.textMuted)}>
                  Manage API keys and integrations
                  {settings?.last_updated && (
                    <span className="ml-2">
                      · Last updated {new Date(settings.last_updated).toLocaleString()}
                      {settings.updated_by && ` by ${settings.updated_by}`}
                    </span>
                  )}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchSettings} disabled={loading}>
                <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                Refresh
              </Button>
            </div>

            {/* Security Notice */}
            <Card className={cn("p-4 border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800")}>
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-800 dark:text-amber-200">Security Notice</h3>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    API keys are encrypted before storage. Values set here override environment variables.
                    Changes take effect immediately. Only share these keys with trusted administrators.
                  </p>
                </div>
              </div>
            </Card>

            {/* OAuth Providers Section */}
            <OAuthProvidersSection colors={colors} />

            {/* GitHub App Section */}
            <GitHubAppSection colors={colors} settings={settings} onRefresh={fetchSettings} />

            {/* Stripe/Payments Section */}
            <StripeSectionCard colors={colors} settings={settings} onRefresh={fetchSettings} />

            {/* API Keys by Category (excluding Payments, Authentication, Integrations) */}
            {Object.entries(groupedKeys).map(([category, keys]) => (
              <div key={category} className="space-y-3">
                <div className="flex items-center gap-2">
                  {React.createElement(CATEGORY_ICONS[category] || Key, {
                    className: cn("w-4 h-4", colors.textMuted)
                  })}
                  <h2 className={cn("text-sm font-semibold uppercase tracking-wider", colors.textMuted)}>
                    {category}
                  </h2>
                </div>
                <div className="space-y-3">
                  {keys.map((apiKey) => (
                    <ApiKeyCard
                      key={apiKey.env_key}
                      apiKey={apiKey}
                      colors={colors}
                      onUpdate={handleUpdate}
                      onTest={handleTest}
                    />
                  ))}
                </div>
              </div>
            ))}

            {/* Info Footer */}
            <Card className={cn("p-4 border", colors.bgSecondary, colors.borderPrimary)}>
              <div className="flex gap-3">
                <Info className={cn("w-5 h-5 flex-shrink-0 mt-0.5", colors.textMuted)} />
                <div className={cn("text-sm", colors.textMuted)}>
                  <p className="font-medium mb-1">How it works:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Keys stored here are encrypted and take priority over .env file values</li>
                    <li>If you remove a key here, the system falls back to the .env file value</li>
                    <li>Changes are applied immediately - no server restart needed</li>
                    <li>The &quot;Test&quot; button performs basic format validation only</li>
                  </ul>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SystemSettingsPage;
